import { formatFileSize } from '@efdigital/ll-admin-v2/utils/files';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const MIN_WIDTH = 800;
const MIN_HEIGHT = 600;

interface Signature {
  Args: {
    onFileSelected: (file: File, title: string) => void;
  };
}

export class ImageUploaderFileInput extends Component<Signature> {
  @tracked isDragging = false;
  @tracked validationError: string = '';
  @tracked previewUrl: string = '';

  handleDragOver = (event: DragEvent): void => {
    event.preventDefault();
    this.isDragging = true;
  };

  handleDragLeave = (event: DragEvent): void => {
    event.preventDefault();
    this.isDragging = false;
  };

  handleDrop = (event: DragEvent): void => {
    event.preventDefault();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    const file = files?.[0];
    if (file) {
      this.handleFileSelect(file);
    }
  };

  handleFileInput = (event: Event): void => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.handleFileSelect(file);
    }
  };

  handleFileSelect(file: File): void {
    this.validationError = '';

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      this.validationError = `File type ${file.type} not supported. Please use JPG, PNG, or WebP.`;
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      this.validationError = `File size (${formatFileSize(file.size)}) exceeds maximum of ${formatFileSize(MAX_FILE_SIZE)}.`;
      return;
    }

    // Auto-populate title from filename if empty
    const title = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
    this.args.onFileSelected(file, title);
  }

  <template>
    {{! File selector / drop zone }}
    <div
      class="border border-2 border-dashed rounded p-5 text-center bg-light
        {{if this.isDragging 'border-primary bg-primary-subtle'}}"
      {{on "dragover" this.handleDragOver}}
      {{on "dragleave" this.handleDragLeave}}
      {{on "drop" this.handleDrop}}
    >
      <iconify-icon
        inline
        icon="ph:box-arrow-down-duotone"
        class="text-muted h1 mb-3"
      />
      <p class="mb-2">Drag and drop an image here</p>
      <p class="text-muted mb-3">or</p>
      <label for="file-input" class="btn btn-primary">
        <iconify-icon inline icon="ph:folder-duotone" class="me-1" />
        Choose File
      </label>
      <input
        type="file"
        id="file-input"
        class="visually-hidden"
        accept="image/jpeg,image/png,image/webp"
        {{on "change" this.handleFileInput}}
      />
      <p class="text-muted mt-3 small">
        Supported formats: JPG, PNG, WebP<br />
        Max file size:
        {{formatFileSize MAX_FILE_SIZE}}<br />
        All preset sizes will be generated automatically after upload.<br />
        Recommended minimum:
        {{MIN_WIDTH}}×{{MIN_HEIGHT}}px
      </p>
    </div>

    {{#if this.validationError}}
      <div class="alert alert-danger">
        {{this.validationError}}
      </div>
    {{/if}}
  </template>
}
